import {
  computed,
  inject,
  Injectable,
  InjectionToken,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { OnyxOptional } from '@onyx/angular';
import { compact, isNumber, uniqBy } from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import type { SimplifiedDelegation } from '../../dashboard/delegations/common/interfaces/delegation';
import type { SimplifiedDriver } from '../../dashboard/drivers/common/interfaces/driver';
import { FleetCategory } from '../../dashboard/fleet/common/enums/fleet-category';
import { FleetTypeHelper } from '../../dashboard/fleet/common/helpers/fleet-type.helper';
import type { TechnicalPoint } from '../../dashboard/fleet/common/interfaces/technical-point';
import { MapRouteFilter } from '../../dashboard/map/common/enums/map-route-filter';
import { LTL_ORDER_POINT_TYPES } from '../../dashboard/orders/common/constants/ltl-order-point-types';
import { OrderPointCategory } from '../../dashboard/orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../dashboard/orders/common/enums/order-point-status';
import type {
  Order,
  SimplifiedOrder,
} from '../../dashboard/orders/common/interfaces/order';
import type { OrderFormPoint } from '../../dashboard/orders/common/interfaces/order-form-point';
import type { OrderPoint } from '../../dashboard/orders/common/interfaces/order-point';
import { OrdersService } from '../../dashboard/orders/common/services/orders.service';
import type { OrderPointFormGroup } from '../../dashboard/orders/order-form/order-points-form/order-point-form/order-point-form.component';
import type { DictionaryType } from '../enums/dictionary-code';
import { DictionaryCode } from '../enums/dictionary-code';
import type { FuelType } from '../enums/fuel-type';
import { RoutePointState } from '../enums/route/route-point-state';
import { RouteHelper } from '../helpers/route.helper';
import { RoutePointValueHelper } from '../helpers/route-point-value.helper';
import type { ActivityPoint } from '../interfaces/common/activity';
import type { RouteContext } from '../interfaces/route/route-context';
import type { RoutePointRealizationCrew } from '../interfaces/route/route-point-realization-crew';
import type { TelematicsDriver } from '../interfaces/telematics/telematics-driver';
import type { TelematicsItem } from '../interfaces/telematics/telematics-item';
import type { TelematicsRoutePoint } from '../interfaces/telematics/telematics-route-point';
import { TelematicsStore } from '../stores/telematics.store';
import { DictionariesService } from './dictionaries.service';

export type RoutePoint =
  OrderPoint | OrderFormPoint | TelematicsRoutePoint | TechnicalPoint;
export type RoutePointKey = string | OrderPointFormGroup;

export const ROUTE_CONTEXT = new InjectionToken<RouteContext>('Route context');

@Injectable()
export class RouteService {
  public readonly context = inject(ROUTE_CONTEXT);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly telematicsStore = inject(TelematicsStore);
  private readonly ordersService = inject(OrdersService);

  public points = signal<RoutePoint[]>([]);
  public filter = signal<MapRouteFilter | string>(MapRouteFilter.ACTIVE_ORDERS);
  public vehicle = signal<
    OnyxOptional<{
      uuid: OnyxOptional<string>;
      fuelType: OnyxOptional<FuelType>;
    }>
  >(null);
  public telematicsItem = signal<OnyxOptional<TelematicsItem>>(null);
  public order = signal<OnyxOptional<SimplifiedOrder>>(null);
  public isDedicated = signal(true);
  public showFirstEmptyRoute = signal(false);

  public add$ = new Subject<{
    point: DictionaryType<DictionaryCode.ORDER_POINT_TYPE>[number];
    index: number;
  }>();
  public move$ = new Subject<{ index: number; direction: -1 | 1 }>();
  public edit$ = new Subject<{ index: number }>();
  public remove$ = new Subject<{ index: number }>();
  public center$ = new Subject<{ id: string }>();

  public addOptions$ = this.dictionariesService
    .getDictionary(DictionaryCode.ORDER_POINT_TYPE)
    .pipe(
      map((options) => options.filter((option) => !option.isSystemOnly)),
      withLatestFrom(toObservable(this.isDedicated)),
      map(([options, isDedicated]) => {
        if (isDedicated) return options;
        return options.filter((option) =>
          LTL_ORDER_POINT_TYPES.includes(option.value),
        );
      }),
      map((options) => [
        {
          id: '0.default',
          subheading: 'labels.addPoint',
          options: options.map((point) => ({
            ...point,
            value: (index: number) => {
              this.add$.next({ point, index });

              setTimeout(() => {
                const section = this.getSection(index);
                if (section) this.togglePointExpanded(section.end - 1, true);
              });
            },
          })),
        },
      ]),
      shareReplay(1),
    );

  public isRouteExpanded = computed(() =>
    this.points().every(
      (point) =>
        this.getPointState(point) !== RoutePointState.DEFAULT &&
        this.isPointExpanded(point),
    ),
  );

  private realizationCrewMap = computed(() => {
    if (this.context === 'order') {
      const activityPoints = this.activityPoints();
      if (!activityPoints) return null;

      return new Map<string, RoutePointRealizationCrew>(
        activityPoints.map(({ point, vehicle, trailer, drivers }) => [
          point.uuid,
          {
            vehicle: { uuid: vehicle.uuid, data: vehicle.generalInformation },
            trailer: trailer
              ? { uuid: trailer.uuid, data: trailer.generalInformation }
              : null,
            drivers: uniqBy(
              compact([drivers.primary, drivers.secondary]),
              ({ uuid }) => uuid,
            ).map(({ uuid, driverData: data }) => ({ uuid, data })),
          },
        ]),
      );
    }

    if (this.context !== 'map') return null;

    const item = this.telematicsItem();
    const points = this.points() as TelematicsRoutePoint[];
    if (!item || !points.length) return null;

    const result = new Map<string, RoutePointRealizationCrew>();

    const vehicle = {
      uuid: item.uuid,
      data: item.generalInformation,
    };

    const trailerMap = new Map<string, RoutePointRealizationCrew['trailer']>();
    const delegationMap = new Map<string, SimplifiedDelegation>();
    const driverMap = new Map<string, TelematicsDriver>();

    for (const item of this.telematicsStore.unfilteredData()) {
      const {
        uuid,
        generalInformation,
        assignedTrailer,
        delegations,
        drivers,
      } = item;

      if (uuid && FleetTypeHelper.isTrailer(generalInformation)) {
        trailerMap.set(uuid, {
          uuid,
          data: generalInformation,
        });
      }

      if (assignedTrailer?.uuid) {
        trailerMap.set(assignedTrailer.uuid, {
          uuid: assignedTrailer.uuid,
          data: assignedTrailer.generalInformation,
        });
      }

      for (const { delegation } of delegations ?? []) {
        if (delegation?.uuid) delegationMap.set(delegation.uuid, delegation);
      }

      for (const driver of drivers ?? []) {
        if (driver?.uuid) driverMap.set(driver.uuid, driver);
      }
    }

    for (const point of points) {
      const pointUuid = RouteHelper.getUuid(point);
      const isCompleted = RouteHelper.isCompleted(point);
      if (!pointUuid || !isCompleted) continue;

      const trailer = point.trailerUuid
        ? (trailerMap.get(point.trailerUuid) ?? null)
        : null;

      let primaryDriver: SimplifiedDriver | null = null;
      let secondaryDriver: SimplifiedDriver | null = null;

      if (
        point.primaryDriverDelegationUuid ===
        point.secondaryDriverDelegationUuid
      ) {
        if (point.primaryDriverDelegationUuid) {
          const delegationData = delegationMap.get(
            point.primaryDriverDelegationUuid,
          );
          if (!delegationData) continue;

          primaryDriver = delegationData.primaryDriver;
          secondaryDriver = delegationData.secondaryDriver;
        }
      } else {
        if (point.primaryDriverDelegationUuid) {
          primaryDriver =
            delegationMap.get(point.primaryDriverDelegationUuid)
              ?.primaryDriver ?? null;
        }
        if (point.secondaryDriverDelegationUuid) {
          secondaryDriver =
            delegationMap.get(point.secondaryDriverDelegationUuid)
              ?.primaryDriver ?? null;
        }
      }

      const drivers: RoutePointRealizationCrew['drivers'] = [];

      if (primaryDriver) {
        drivers.push({
          uuid: primaryDriver.uuid,
          data: primaryDriver.driverData,
        });
      }

      if (secondaryDriver && secondaryDriver.uuid !== primaryDriver?.uuid) {
        drivers.push({
          uuid: secondaryDriver.uuid,
          data: secondaryDriver.driverData,
        });
      }

      result.set(pointUuid, {
        vehicle,
        trailer,
        drivers,
      });
    }
    return result;
  });

  public showAdd = computed(() => this.add$.observed);
  public showMove = computed(() => this.move$.observed);
  public showEdit = computed(() => this.edit$.observed);
  public showRemove = computed(() => this.remove$.observed);
  public showCenter = computed(() => this.center$.observed);

  public currentPoint = computed(() =>
    RouteHelper.getCurrentPoint(this.points(), this.order()),
  );

  private pointsState = signal(new Map<RoutePointKey, RoutePointState>());
  private pointsExpanded = signal(new Set<RoutePointKey>());
  private activityOrder$ = new Subject<Order>();
  private activityPoints = toSignal(
    this.activityOrder$.pipe(
      switchMap((order) => this.getActivityPoints(order)),
    ),
    { initialValue: null },
  );

  constructor() {
    const currentPointKey = computed(() =>
      this.currentPoint() ? this.getKey(this.currentPoint()!) : null,
    );

    toObservable(currentPointKey)
      .pipe(
        debounceTime(1),
        distinctUntilChanged(),
        filter((key) => key != null && this.context !== 'swapPanel'),
      )
      .subscribe(() => this.toggleSection(this.currentPoint()!, true));
  }

  public getRealizationCrew(
    routePoint: RoutePoint | number,
  ): RoutePointRealizationCrew | null {
    const map = this.realizationCrewMap();
    if (!map) return null;

    const point = isNumber(routePoint)
      ? this.points().at(routePoint)
      : routePoint;
    if (!point) return null;

    const uuid = RouteHelper.getUuid(point);
    return uuid ? (map.get(uuid) ?? null) : null;
  }

  public getPointState(point: RoutePoint | number): RoutePointState {
    const key = this.getKey(point);
    if (!key) return RoutePointState.DEFAULT;

    return this.pointsState().get(key) ?? RoutePointState.DEFAULT;
  }

  public togglePointState(
    point: RoutePoint | number,
    force?: RoutePointState,
  ): void {
    const key = this.getKey(point);
    if (!key) return;

    this.pointsState.update((pointsState) => {
      const currentState = this.getPointState(point);
      force ??=
        currentState === RoutePointState.DEFAULT
          ? RoutePointState.DETAILS
          : RoutePointState.DEFAULT;

      return new Map(pointsState).set(key, force);
    });

    if (this.pointsState().get(key) === RoutePointState.DEFAULT) return;
    if (this.isMainPoint(point)) return;

    const section = this.getSection(point);
    if (!section) return;

    const { end } = section;
    this.togglePointExpanded(end - 1, true);
  }

  public isPointExpanded(point: RoutePoint | number): boolean {
    const key = this.getKey(point);
    if (!key) return false;

    return this.pointsExpanded().has(key);
  }

  public togglePointExpanded(
    point: RoutePoint | number,
    force?: boolean,
  ): void {
    const key = this.getKey(point);
    if (!key) return;

    this.pointsExpanded.update((pointsExpanded) => {
      force ??= !this.isPointExpanded(point);
      force ? pointsExpanded.add(key) : pointsExpanded.delete(key);
      return new Set(pointsExpanded);
    });
  }

  public toggleSection(point: RoutePoint | number, force?: boolean): void {
    const section = this.getSection(point);
    if (!section) return;

    const { start, end } = section;
    const sectionPoints = this.points().slice(start, end);
    force ??= !sectionPoints.every(
      (point) => this.getPointState(point) !== RoutePointState.DEFAULT,
    );

    for (const point of this.points()) {
      if (!sectionPoints.includes(point)) {
        this.togglePointState(point, RoutePointState.DEFAULT);
        this.togglePointExpanded(point, false);
        continue;
      }

      this.togglePointState(
        point,
        force ? RoutePointState.DETAILS : RoutePointState.DEFAULT,
      );
      this.togglePointExpanded(point, force);
    }
  }

  public expandPreviousSection(): void {
    const currentSection = this.getCurrentSection();
    const previousPoint = this.points().at(
      currentSection?.length === 1 ? -1 : (currentSection?.start ?? 0),
    );
    if (!previousPoint) return;

    this.toggleSection(previousPoint, true);
  }

  public expandNextSection(): void {
    const currentSection = this.getCurrentSection();
    const nextPoint = this.points().at(
      (currentSection?.end ?? this.points().length) % this.points().length,
    );
    if (!nextPoint) return;

    this.toggleSection(nextPoint, true);
  }

  public toggleRouteExpanded(force?: boolean): void {
    const isExpanded = force ?? !this.isRouteExpanded();
    for (const point of this.points()) {
      this.togglePointState(
        point,
        isExpanded ? RoutePointState.DETAILS : RoutePointState.DEFAULT,
      );
      this.togglePointExpanded(point, isExpanded);
    }
  }

  public getCycleIndex(point: RoutePoint): number {
    if ('cycleIndex' in point) return point.cycleIndex;

    const cycleIndex = this.telematicsStore
      .cycleIndexByPointUuid()
      .get(RouteHelper.getUuid(point));
    if (cycleIndex != null) return cycleIndex;

    return this.points()
      .filter((point) => RouteHelper.isIncludedInOrder(point))
      .findIndex((p) => this.isEqual(p, point));
  }

  public isFirst(point: RoutePoint): boolean {
    return this.isEqual(point, 0);
  }

  public isLast(point: RoutePoint): boolean {
    return this.isEqual(point, -1);
  }

  public isCurrent(point: RoutePoint): boolean {
    return this.isEqual(point, this.currentPoint());
  }

  public isEmpty(point: RoutePoint): boolean {
    return RouteHelper.isEmpty(this.points(), point);
  }

  public isSystemPoint(point: RoutePoint): boolean {
    return (
      RoutePointValueHelper.getValue(point)?.category ===
      OrderPointCategory.PICKUP
    );
  }

  public hasAdd(point: RoutePoint | number): boolean {
    if (!this.showAdd()) return false;

    const routePoint = isNumber(point) ? this.points().at(point) : point;
    if (!routePoint) return false;

    const routePointStatus = RouteHelper.getStatus(routePoint);
    return routePointStatus !== OrderPointStatus.COMPLETED;
  }

  public hasMove(point: RoutePoint | number, direction: -1 | 1): boolean {
    if (!this.showMove()) return false;

    const routePoint = isNumber(point) ? this.points().at(point) : point;
    if (!routePoint) return false;
    if (this.isSystemPoint(routePoint)) return false;

    if (RouteHelper.isCompleted(routePoint)) return false;

    const index = isNumber(point) ? point : this.getIndex(routePoint);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= this.points().length) return false;

    const swapPoint = this.points()[swapIndex];
    if (this.isSystemPoint(swapPoint)) return false;

    const swapPointStatus = RouteHelper.getStatus(swapPoint);

    return !swapPointStatus || swapPointStatus === OrderPointStatus.TO_DO;
  }

  public hasEdit(point: RoutePoint | number): boolean {
    if (!this.showEdit()) return false;

    const routePoint = isNumber(point) ? this.points().at(point) : point;
    if (!routePoint) return false;

    const routePointStatus = RouteHelper.getStatus(routePoint);
    if (!routePointStatus) return true;

    return routePointStatus === OrderPointStatus.TO_DO;
  }

  public hasRemove(point: RoutePoint | number): boolean {
    if (!this.showRemove()) return false;

    const routePoint = isNumber(point) ? this.points().at(point) : point;
    if (!routePoint) return false;
    if (this.isSystemPoint(routePoint)) return false;

    const routePointStatus = RouteHelper.getStatus(routePoint);
    if (!routePointStatus) return true;

    return routePointStatus === OrderPointStatus.TO_DO;
  }

  public getIndex(point: RoutePoint): number {
    return this.points().findIndex((p) => this.isEqual(p, point));
  }

  public getKey(point: RoutePoint | number): RoutePointKey | null {
    return RouteHelper.getKey(this.points(), point);
  }

  public isEqual(
    a: OnyxOptional<RoutePoint | number>,
    b: OnyxOptional<RoutePoint | number>,
  ): boolean {
    return RouteHelper.isEqual(this.points(), a, b);
  }

  public getTelematicsItemByRoutePoint(
    item: TelematicsItem,
    routePoint: RoutePoint,
  ): TelematicsItem {
    let currentSemiTrailer =
      item.assignedTrailer?.generalInformation.category ===
      FleetCategory.SEMI_TRAILER
        ? item.assignedTrailer
        : null;

    for (const point of this.points().slice(0, this.getIndex(routePoint))) {
      const loadedSemiTrailer =
        'order' in point
          ? point.order?.basicInformation?.loadedSemiTrailer
          : null;
      if (!loadedSemiTrailer) continue;

      const uuid = RoutePointValueHelper.getValue(point).uuid;
      const { pickupPointUuid, dropPointUuid, data } = loadedSemiTrailer;

      if (pickupPointUuid === uuid) {
        currentSemiTrailer = data;
      } else if (dropPointUuid === uuid) {
        currentSemiTrailer = null;
      }
    }

    return {
      ...item,
      assignedTrailer: currentSemiTrailer,
    };
  }

  public loadActivityPoints(order: Order): void {
    this.activityOrder$.next(order);
  }

  private getActivityPoints(order: Order): Observable<ActivityPoint[] | null> {
    if (!order.points.some((point) => RouteHelper.isCompleted(point))) {
      return of([]);
    }

    return this.ordersService.getOrderActivity(order.uuid).pipe(
      map(({ points }) => points),
      catchError(() => of(null)),
    );
  }

  private getCurrentSection(): ReturnType<typeof this.getSection> {
    const activePoint = this.points().findLast(
      (point) => this.getPointState(point) !== RoutePointState.DEFAULT,
    );
    if (!activePoint) return null;

    return this.getSection(activePoint);
  }

  private getSection(
    point: RoutePoint | number,
  ): { start: number; end: number; length: number } | null {
    const index = isNumber(point) ? point : this.points().indexOf(point);
    if (index === -1) return null;

    const start = this.points()
      .slice(0, index)
      .findLastIndex(this.isMainPoint.bind(this));
    const end = this.points()
      .slice(index)
      .findIndex(this.isMainPoint.bind(this), index + 1);

    return {
      start: start === -1 ? 0 : start,
      end: end === -1 ? this.points().length : end + index + 1,
      length: end - start,
    };
  }

  private isMainPoint(point: RoutePoint | number): boolean {
    point = isNumber(point) ? this.points()[point] : point;
    if (!point) return false;

    return (
      RouteHelper.isIncludedInOrder(point) ||
      this.isFirst(point) ||
      this.isLast(point)
    );
  }
}
